import type { FC, ReactElement } from 'react';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { sbpPaymentSmall, copySuccessGreen, copyGreen } from '@images';
import API from '@api';
import { Button, Picture, Modal } from '@ui';
import useWindowSize from '@customHooks/useWindowSize';
import { getDevice } from '@utils/getDevice';
import useClipboard from '@customHooks/useClipboard';
import { Link } from '@compat/router';
import './index.scss';
import { cutFloatNumber } from '@utils/floatNumbers';
import useCheckIsPaidInteval from '@customHooks/useCheckIsPaidInteval';
import type { GetSbpLinkQuery } from '@schema/cart';

const LazyQr = lazy(async () => await import('./SbpQr'));

const banksQueue = [
  'Сбербанк',
  'Тинькофф',
  'Альфа-Банк',
  'ВТБ',
  'Райффайзен Банк',
  'Открытие',
  'Газпромбанк',
  'МКБ',
  'Юникредит Банк'
];

interface SbpButtonProps {
  order: number;
  updatePaymentInfo?: () => void;
  isOrderPage?: boolean;
  orderIsPayedPartiallyWithMoneyOrBonuses?: boolean;
  payAmount?: number;
  btnComponent?: ({ onClick }) => ReactElement<{ onClick: () => void }> | null;
}

export const SbpButton: FC<SbpButtonProps> = ({
  order,
  updatePaymentInfo,
  isOrderPage,
  orderIsPayedPartiallyWithMoneyOrBonuses,
  payAmount,
  btnComponent: BtnComponent = null
}) => {
  const { isMobile } = useWindowSize();
  const [data, setData] = useState<
    | NonNullable<
        NonNullable<GetSbpLinkQuery>['getSBPLink']['apiResponse']
      >['data']
    | null
  >(null);
  const [isPopupOpened, setIsPopupOpened] = useState(false);
  const [error, setError] = useState(false);
  const [isBtnClicked, setIsBtnClicked] = useState(false);

  const { isCopied, copyPromocode } = useClipboard({
    text: data?.link,
    notificationText: 'Ссылка успешно скопирована!'
  });

  useCheckIsPaidInteval({
    orderId: +order,
    successCallback: updatePaymentInfo
  });

  const fetchErrorHandler = (err): void => {
    err && console.log(err);
    setError(true);
    setIsPopupOpened(true);
  };
  const onBtnClick = useCallback((): Promise<void> | undefined => {
    if (isBtnClicked) {
      return;
    }
    setIsBtnClicked(true);
    return API.getSBPLink({ order: +order })
      .then(res => {
        if (res.getSBPLink?.error || res?.getSBPLink?.apiResponse?.error) {
          fetchErrorHandler(res.getSBPLink?.error);
          return;
        }
        if (res?.getSBPLink?.apiResponse?.data) {
          setData(res.getSBPLink.apiResponse.data);
        }
        setIsPopupOpened(true);
      })
      .catch(fetchErrorHandler);
  }, [isBtnClicked, order]);

  const onLinkClick = (e): void => {
    e.stopPropagation();
    copyPromocode();
  };

  const onCloseModal = (): void => {
    setIsBtnClicked(false);
    setIsPopupOpened(false);
    if (error) {
      return;
    }
    if (data?.payment_id) {
      API.checkSBPStatus({ paymentId: +data.payment_id }).then(res => {
        if (res.checkSBPStatuses.success && updatePaymentInfo) {
          updatePaymentInfo();
        }
      });
    }
  };

  const onBankIconClick = (bankLink): void => {
    const appWindow = window.open(bankLink, '_blank');
    setTimeout(function () {
      if (appWindow) {
        appWindow.location = data?.link ?? '';
      }
    }, 1000);
  };

  const device = getDevice();

  const btn = useMemo(
    () => (
      <div
        onClick={onBtnClick}
        className={`SbpButton${isBtnClicked ? ' SbpButton--disabled' : ''}${
          isOrderPage ? ' SbpButton--rounded' : ''
        }`}
      >
        <Picture
          pictureClassName='SbpButton__icon'
          icon={sbpPaymentSmall}
          height='24'
          width='24'
        />
        <span>
          {orderIsPayedPartiallyWithMoneyOrBonuses ? 'Доплатить' : 'Оплатить'}{' '}
          {!!payAmount && `${payAmount} ₽`}
        </span>
        {/* <Picture pictureClassName='SbpButton__icon--white' icon={sbpPaymentWhite} height='24' width='42' /> */}
      </div>
    ),
    [
      isBtnClicked,
      isOrderPage,
      onBtnClick,
      orderIsPayedPartiallyWithMoneyOrBonuses,
      payAmount
    ]
  );
  return (
    <>
      {BtnComponent?.({ onClick: onBtnClick }) || btn}
      {isPopupOpened && (
        <Modal
          framed
          placementMobile='center'
          open={isPopupOpened}
          onClose={onCloseModal}
        >
          <div className={'SbpModal'}>
            {error ? (
              <div className='SbpModal__error'>
                <div className='SbpModal__error__heading'>
                  Оплата недоступна
                </div>
                <div className='SbpModal__error__text'>
                  Выбранный вид оплаты сейчас невозможен.
                  <br /> Другой способ оплаты будет доступен в&nbsp;
                  <Link to='/cabinet/orders'>истории&nbsp;заказов</Link>.
                </div>
                <Button
                  onClick={onCloseModal}
                  rank='primary'
                  colour='red'
                  size={'medium'}
                  text={'Понятно, спасибо'}
                />
              </div>
            ) : (
              <>
                <div className='SbpModal__heading'>Оплата по СБП</div>
                {!isMobile && (
                  <div className='SbpModal__text'>
                    Для оплаты по СБП отсканируйте QR-код в&nbsp;мобильном
                    приложении банка или штатной камерой телефона
                  </div>
                )}
                <Suspense fallback={<div className='SbpModal__qr-loader' />}>
                  <LazyQr link={data?.link ?? ''} />
                </Suspense>
                {isMobile && (
                  <>
                    <div onClick={onLinkClick} className='SbpModal__link'>
                      <span className='SbpModal__link__text'>
                        {data?.link ?? ''}
                      </span>{' '}
                      <Picture
                        height='20'
                        icon={isCopied ? copySuccessGreen : copyGreen}
                      />
                    </div>
                    <Button
                      onClick={() => window.open(data?.link ?? '', '_blank')}
                      rank='primary'
                      colour='red'
                      size={'medium'}
                      text={`Оплатить ${cutFloatNumber(data?.amount ?? 0)} ₽`}
                    />
                    {(device === 'android' || device === 'ios') && (
                      <div className='SbpModal__banks'>
                        <div className='SbpModal__banks__heading'>
                          Оплатить через приложения банка
                        </div>
                        <div className='SbpModal__banks__list'>
                          {banksQueue.map(item => {
                            const i = data?.banks?.find(
                              j =>
                                j.title?.toLowerCase() === item?.toLowerCase()
                            );
                            if (!i) {
                              return null;
                            }
                            return (
                              <div
                                key={item}
                                onClick={() => {
                                  onBankIconClick(i?.[device]?.link);
                                }}
                              >
                                <img
                                  width='56'
                                  height='56'
                                  src={i.image ?? ''}
                                  alt={item}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
